import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Smartphone,
    Lock,
    Eye,
    EyeOff,
    LogIn,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import config from "../config";

export default function LoginPage() {
    const navigate = useNavigate();

    /*
     * login() should save only user information.
     * Never save the JWT in localStorage or sessionStorage.
     *
     * The PHP backend stores the JWT in an HttpOnly cookie.
     */
    const { login } = useAuth();

    const [mobile, setMobile] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(true);
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({
        mobile: "",
        password: "",
    });

    const validate = () => {
        const newErrors = {
            mobile: "",
            password: "",
        };

        if (!/^[0-9]{10}$/.test(mobile)) {
            newErrors.mobile =
                "Please enter a valid 10-digit mobile number.";
        }

        if (!password || password.length < 4) {
            newErrors.password = "Please enter your password.";
        }

        setErrors(newErrors);

        return !newErrors.mobile && !newErrors.password;
    };

    /*
     * Safely parse the PHP API response.
     *
     * This allows you to see the actual PHP response when the server
     * returns an error page, warning, or HTML instead of JSON.
     */
    const parseJsonResponse = async (response) => {
        const responseText = await response.text();

        if (!responseText.trim()) {
            throw new Error(
                `Server returned an empty response. HTTP ${response.status}.`
            );
        }

        try {
            return JSON.parse(responseText);
        } catch (error) {
            console.error("Invalid PHP response:", responseText);

            throw new Error(
                "The server returned an invalid response. Check the PHP error log."
            );
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validate() || loading) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(
                `${config.API_URL}/auth.php`,
                {
                    method: "POST",

                    /*
                     * Required because the PHP backend stores the JWT
                     * in an HttpOnly cookie.
                     */
                    credentials: "include",

                    /*
                     * Avoid receiving a cached login response.
                     */
                    cache: "no-store",

                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },

                    body: JSON.stringify({
                        mobile,
                        password,

                        /*
                         * PHP can use this to choose the JWT expiry:
                         *
                         * remember = true  -> longer cookie expiry
                         * remember = false -> session cookie or shorter expiry
                         */
                        remember,
                    }),
                }
            );

            const result = await parseJsonResponse(response);

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                    `Login failed. HTTP ${response.status}.`
                );
            }

            if (!result.user) {
                throw new Error(
                    "Login succeeded, but user information was not returned."
                );
            }

            /*
             * Store only non-sensitive user information.
             *
             * The JWT itself remains inside the HttpOnly cookie.
             */
            login(result.user, remember);

            toast.success(
                result.message || "Login successful."
            );

            navigate("/", {
                replace: true,
            });
        } catch (error) {
            console.error("JWT login error:", error);

            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to connect to the server."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="
                relative
                min-h-[calc(100vh-70px-40px)]
                flex
                items-center
                justify-center
                px-4
                py-2
            "
            data-testid="login-page"
        >
            {/* Layered glow */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                    className="
                        h-[420px]
                        w-[420px]
                        rounded-full
                        bg-gradient-to-tr
                        from-[#3BC7FF]/25
                        to-[#7B61FF]/25
                        blur-3xl
                    "
                />
            </div>

            <div className="relative w-full max-w-md fade-in-up">
                <div className="glass-card rounded-3xl p-7 sm:p-8">
                    <div className="flex flex-col items-center text-center mb-6">
                        <div
                            className="
                                h-12
                                w-12
                                rounded-2xl
                                btn-gradient
                                flex
                                items-center
                                justify-center
                                text-white
                                shadow-lg
                            "
                        >
                            <LogIn className="h-5 w-5" />
                        </div>

                        <h1
                            className="
                                mt-4
                                text-2xl
                                sm:text-3xl
                                font-display
                                font-bold
                                tracking-tight
                            "
                        >
                            Sign in to{" "}
                            <span className="text-gradient-brand">
                                Smart Solutions
                            </span>
                        </h1>

                        <p className="mt-1.5 text-sm text-muted-foreground">
                            Access your services dashboard securely.
                        </p>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        className="space-y-4"
                        noValidate
                    >
                        {/* Mobile number */}
                        <div>
                            <label
                                htmlFor="mobile"
                                className="
                                    text-xs
                                    font-semibold
                                    tracking-[0.2em]
                                    uppercase
                                    text-muted-foreground
                                "
                            >
                                Mobile Number
                            </label>

                            <div className="mt-2 relative">
                                <Smartphone
                                    className="
                                        pointer-events-none
                                        absolute
                                        left-4
                                        top-1/2
                                        -translate-y-1/2
                                        h-4
                                        w-4
                                        text-muted-foreground
                                    "
                                />

                                <input
                                    id="mobile"
                                    name="mobile"
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    placeholder="Enter 10-digit mobile"
                                    maxLength={10}
                                    value={mobile}
                                    disabled={loading}
                                    data-testid="login-mobile-input"
                                    onChange={(event) => {
                                        const value = event.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 10);

                                        setMobile(value);

                                        if (errors.mobile) {
                                            setErrors((current) => ({
                                                ...current,
                                                mobile: "",
                                            }));
                                        }
                                    }}
                                    className="
                                        w-full
                                        h-12
                                        pl-11
                                        pr-4
                                        rounded-xl
                                        bg-background/60
                                        border
                                        border-border
                                        focus:border-primary
                                        focus:ring-2
                                        focus:ring-primary/25
                                        outline-none
                                        text-sm
                                        font-mono
                                        tracking-wider
                                        transition
                                        disabled:opacity-60
                                    "
                                />
                            </div>

                            {errors.mobile && (
                                <p
                                    className="mt-1.5 text-xs text-destructive"
                                    data-testid="login-mobile-error"
                                >
                                    {errors.mobile}
                                </p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label
                                htmlFor="password"
                                className="
                                    text-xs
                                    font-semibold
                                    tracking-[0.2em]
                                    uppercase
                                    text-muted-foreground
                                "
                            >
                                Password
                            </label>

                            <div className="mt-2 relative">
                                <Lock
                                    className="
                                        pointer-events-none
                                        absolute
                                        left-4
                                        top-1/2
                                        -translate-y-1/2
                                        h-4
                                        w-4
                                        text-muted-foreground
                                    "
                                />

                                <input
                                    id="password"
                                    name="password"
                                    type={showPw ? "text" : "password"}
                                    autoComplete="current-password"
                                    placeholder="Enter password"
                                    value={password}
                                    disabled={loading}
                                    data-testid="login-password-input"
                                    onChange={(event) => {
                                        setPassword(event.target.value);

                                        if (errors.password) {
                                            setErrors((current) => ({
                                                ...current,
                                                password: "",
                                            }));
                                        }
                                    }}
                                    className="
                                        w-full
                                        h-12
                                        pl-11
                                        pr-11
                                        rounded-xl
                                        bg-background/60
                                        border
                                        border-border
                                        focus:border-primary
                                        focus:ring-2
                                        focus:ring-primary/25
                                        outline-none
                                        text-sm
                                        transition
                                        disabled:opacity-60
                                    "
                                />

                                <button
                                    type="button"
                                    disabled={loading}
                                    onClick={() => {
                                        setShowPw((current) => !current);
                                    }}
                                    aria-label={
                                        showPw
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                    data-testid="login-password-toggle"
                                    className="
                                        absolute
                                        right-3
                                        top-1/2
                                        -translate-y-1/2
                                        h-8
                                        w-8
                                        rounded-lg
                                        flex
                                        items-center
                                        justify-center
                                        text-muted-foreground
                                        hover:text-foreground
                                        hover:bg-foreground/[0.06]
                                        transition
                                        disabled:opacity-60
                                    "
                                >
                                    {showPw ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>

                            {errors.password && (
                                <p
                                    className="mt-1.5 text-xs text-destructive"
                                    data-testid="login-password-error"
                                >
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-between pt-1">
                            <label
                                htmlFor="remember"
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    text-xs
                                    text-muted-foreground
                                    cursor-pointer
                                    select-none
                                "
                            >
                                <input
                                    id="remember"
                                    name="remember"
                                    type="checkbox"
                                    checked={remember}
                                    disabled={loading}
                                    data-testid="login-remember-checkbox"
                                    onChange={(event) => {
                                        setRemember(event.target.checked);
                                    }}
                                    className="
                                        h-4
                                        w-4
                                        rounded
                                        border-border
                                        accent-[color:hsl(var(--primary))]
                                    "
                                />

                                Remember me
                            </label>

                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => navigate("/forgot-password")}
                                data-testid="login-forgot-btn"
                                className="
                                    text-xs
                                    font-medium
                                    text-primary
                                    hover:underline
                                    underline-offset-4
                                    disabled:opacity-60
                                "
                            >
                                Forgot password?
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="
                                w-full
                                h-12
                                rounded-full
                                btn-gradient
                                ripple
                                text-white
                                font-semibold
                                flex
                                items-center
                                justify-center
                                gap-2
                                disabled:opacity-70
                                disabled:cursor-not-allowed
                            "
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Signing in…
                                </>
                            ) : (
                                <>
                                    <LogIn className="h-4 w-4" />
                                    Sign in
                                </>
                            )}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-xs text-muted-foreground">
                        Need help?{" "}
                        <Link
                            to="/contact"
                            className="text-primary hover:underline"
                        >
                            Contact support
                        </Link>
                    </p>
                </div>

                <p
                    className="
                        mt-4
                        text-center
                        text-[11px]
                        uppercase
                        tracking-[0.28em]
                        text-muted-foreground
                    "
                >
                    Secured by Smart Solutions
                </p>
            </div>
        </section>
    );
}

